import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { homePath } from "../lib/api";
import {
  ChartLineUp, ArrowRight, Calculator, Receipt, Bank, TrendUp, UsersThree,
  ShieldCheck, FileText, ChartPieSlice, CheckCircle, Buildings, EnvelopeSimple, PaperPlaneRight
} from "@phosphor-icons/react";

const rupee = (n) => "₹" + Math.round(n || 0).toLocaleString("en-IN");

const PACKAGES = [
  { name: "Starter", price: "₹4,999", period: "/mo", tag: "Solo & early-stage",
    features: ["GST & ITR filing", "Basic compliance calendar", "Document vault", "Email support"] },
  { name: "Growth", price: "₹14,999", period: "/mo", popular: true, tag: "Growing SMEs",
    features: ["Everything in Starter", "Dedicated CFO agent", "TDS + ROC + Payroll", "MIS & cash-flow reports", "Live chat & meetings"] },
  { name: "Professional", price: "₹29,999", period: "/mo", tag: "Scaling businesses",
    features: ["Everything in Growth", "CMA & loan advisory", "AI Virtual CFO insights", "Priority turnaround", "Quarterly board pack"] },
  { name: "Enterprise", price: "Custom", period: "", tag: "Groups & large cos.",
    features: ["Everything in Professional", "Multi-entity consolidation", "Dedicated CA team", "Audit support", "SLA-backed delivery"] },
];

export default function Landing() {
  const nav = useNavigate();
  const { user } = useAuth();
  const goPortal = () => nav(user ? homePath(user.role) : "/login");

  return (
    <div className="bg-[#faf9f6] text-[#111]">
      {/* Nav */}
      <header className="sticky top-0 z-40 bg-[#faf9f6]/90 backdrop-blur-md border-b border-black/5">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2">
            <ChartLineUp size={26} weight="fill" color="#002FA7" />
            <span className="font-head font-black tracking-tight text-lg">VIRTUALEENA</span>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm">
            <a href="#packages" className="hover:text-[#002FA7] transition-colors">Packages</a>
            <a href="#calculators" className="hover:text-[#002FA7] transition-colors">Calculators</a>
          </nav>
          <button data-testid="landing-login-btn" onClick={goPortal}
            className="bg-[#002FA7] hover:bg-[#00227A] text-white text-sm px-5 py-2.5 rounded-sm inline-flex items-center gap-2 transition-colors">
            {user ? "Go to Portal" : "Client Login"} <ArrowRight size={15} />
          </button>
        </div>
      </header>

      {/* Hero */}
      <section data-testid="landing-hero" className="bg-[#111111] text-white">
        <div className="max-w-6xl mx-auto px-6 py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="uppercase tracking-[0.3em] text-[#FFD700] text-xs mb-5">Your Virtual CFO</p>
            <h1 className="font-head font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-6">
              Finance, tax &amp; compliance—<span className="text-[#002FA7] bg-white px-2">handled.</span>
            </h1>
            <p className="text-white/70 text-lg max-w-md mb-8">
              Virtualeena gives your business a full CFO team on demand—GST, ITR, TDS, ROC, payroll and board-ready reports, all in one portal.
            </p>
            <div className="flex flex-wrap gap-3">
              <button onClick={goPortal} className="bg-[#002FA7] hover:bg-[#00227A] text-white px-6 py-3 rounded-sm inline-flex items-center gap-2 transition-colors">
                Get Started <ArrowRight size={16} />
              </button>
              <a href="#calculators" className="border border-white/25 hover:bg-white/10 px-6 py-3 rounded-sm inline-flex items-center gap-2 transition-colors">
                <Calculator size={16} /> Free Calculators
              </a>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[{ Icon: ShieldCheck, t: "Compliance Score", s: "Never miss a deadline" },
              { Icon: FileText, t: "Document Vault", s: "OCR + AI categorized" },
              { Icon: ChartPieSlice, t: "MIS & Cash Flow", s: "Board-ready PDFs" },
              { Icon: UsersThree, t: "Dedicated Agent", s: "Real CA experts" }].map((c, i) => (
              <div key={i} className="border border-white/10 rounded-sm p-5 bg-white/[0.03]">
                <c.Icon size={26} color="#FFD700" weight="fill" />
                <p className="font-head font-bold mt-3">{c.t}</p>
                <p className="text-white/50 text-sm">{c.s}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Packages */}
      <section id="packages" className="max-w-6xl mx-auto px-6 py-20">
        <p className="uppercase tracking-[0.3em] text-[#002FA7] text-xs mb-2">Pricing</p>
        <h2 className="font-head font-black text-3xl sm:text-4xl mb-10">Packages that grow with you</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {PACKAGES.map((p, i) => (
            <div key={p.name} data-testid={`pkg-${i}`}
              className={`rounded-sm p-6 flex flex-col ${p.popular ? "bg-[#111111] text-white ring-2 ring-[#FFD700]" : "bg-white border border-black/10"}`}>
              {p.popular && <span className="self-start text-[10px] uppercase font-bold tracking-wider bg-[#FFD700] text-[#111] px-2 py-0.5 rounded-sm mb-3">Most Popular</span>}
              <p className="font-head font-bold text-lg">{p.name}</p>
              <p className={`text-xs mb-4 ${p.popular ? "text-white/50" : "text-[#888]"}`}>{p.tag}</p>
              <p className="font-head font-black text-3xl mb-5">{p.price}<span className={`text-sm font-normal ${p.popular ? "text-white/50" : "text-[#888]"}`}>{p.period}</span></p>
              <ul className="space-y-2 flex-1 mb-6">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <CheckCircle size={16} weight="fill" className={p.popular ? "text-[#FFD700] mt-0.5" : "text-[#00A86B] mt-0.5"} /> {f}
                  </li>
                ))}
              </ul>
              <button data-testid={`pkg-cta-${i}`} onClick={() => nav(user ? homePath(user.role) : `/login?plan=${encodeURIComponent(p.name)}`)}
                className={`w-full py-2.5 rounded-sm text-sm transition-colors ${p.popular ? "bg-[#FFD700] text-[#111] hover:brightness-95" : "bg-[#002FA7] text-white hover:bg-[#00227A]"}`}>
                Choose {p.name}
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* Calculators */}
      <section id="calculators" className="bg-white border-y border-black/5">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <p className="uppercase tracking-[0.3em] text-[#002FA7] text-xs mb-2">Free Tools</p>
          <h2 className="font-head font-black text-3xl sm:text-4xl mb-10">Financial Calculators</h2>
          <Calculators />
        </div>
      </section>

      {/* Lead capture */}
      <section id="consult" className="max-w-6xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <p className="uppercase tracking-[0.3em] text-[#002FA7] text-xs mb-2">Talk to us</p>
          <h2 className="font-head font-black text-3xl sm:text-4xl mb-4">Book a free consultation</h2>
          <p className="text-[#555] max-w-md">Tell us about your business and a Virtualeena CFO expert will reach out within one business day to map your GST, tax and compliance needs.</p>
        </div>
        <LeadForm />
      </section>

      <footer className="bg-[#111111] text-white/60 text-sm">
        <div className="max-w-6xl mx-auto px-6 py-10 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-white"><ChartLineUp size={20} weight="fill" color="#002FA7" /> <span className="font-head font-black">VIRTUALEENA</span></div>
          <p>© {new Date().getFullYear()} Virtualeena · Virtual CFO Platform</p>
        </div>
      </footer>
    </div>
  );
}

function IncomeTaxCalc() {
  const [income, setIncome] = useState(1500000);
  const inc = Math.max(0, Number(income) || 0);
  // New regime FY 2025-26 slabs (post standard deduction handled by user input as taxable)
  const slabs = [[400000, 0], [800000, 5], [1200000, 10], [1600000, 15], [2000000, 20], [2400000, 25], [Infinity, 30]];
  let tax = 0, prev = 0;
  for (const [upto, r] of slabs) {
    if (inc > prev) { tax += (Math.min(inc, upto) - prev) * r / 100; prev = upto; } else break;
  }
  // Sec 87A rebate: taxable income up to 12,00,000 → nil tax (new regime FY25-26)
  const rebate = inc <= 1200000 ? tax : 0;
  const afterRebate = tax - rebate;
  const cess = afterRebate * 0.04;
  const total = afterRebate + cess;
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div className="space-y-4">
        <Field label="Taxable income (₹/year)"><input data-testid="it-income" type="number" value={income} onChange={(e) => setIncome(e.target.value)} className={numCls} /></Field>
        <p className="text-xs text-[#888]">New regime, FY 2025-26. Slabs: 0-4L nil, 4-8L 5%, 8-12L 10%, 12-16L 15%, 16-20L 20%, 20-24L 25%, above 30%. Full §87A rebate up to ₹12L. Includes 4% cess.</p>
      </div>
      <div data-testid="it-result" className="bg-black/[0.02] rounded-sm p-5">
        <ResultRow k="Tax before rebate" v={rupee(tax)} />
        <ResultRow k="§87A rebate" v={rebate ? `− ${rupee(rebate)}` : "—"} />
        <ResultRow k="Health & Edu. cess (4%)" v={rupee(cess)} />
        <ResultRow k="Total tax payable" v={rupee(total)} strong />
      </div>
    </div>
  );
}

/* ---------------- Lead form ---------------- */
function LeadForm() {
  const [f, setF] = useState({ name: "", email: "", phone: "", message: "", plan: "", source: "Consultation Form" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email) { toast.error("Name and email are required"); return; }
    setBusy(true);
    try { await api.post("/leads", f); setSent(true); toast.success("Request received — we'll be in touch!"); }
    catch { toast.error("Could not send. Please try again."); }
    finally { setBusy(false); }
  };
  if (sent) return (
    <div data-testid="lead-success" className="card p-8 text-center">
      <CheckCircle size={36} weight="fill" className="mx-auto mb-3 text-[#00A86B]" />
      <p className="font-head font-bold text-lg">Thanks, {f.name.split(" ")[0]}!</p>
      <p className="text-sm text-[#888] mt-1">Our team will reach out shortly.</p>
    </div>
  );
  const cls = "w-full border border-black/20 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]";
  return (
    <form data-testid="lead-form" onSubmit={submit} className="card p-6 space-y-3">
      <input data-testid="lead-name" className={cls} placeholder="Your name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
      <div className="grid sm:grid-cols-2 gap-3">
        <input data-testid="lead-email" type="email" className={cls} placeholder="Work email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        <input data-testid="lead-phone" className={cls} placeholder="Phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
      </div>
      <select data-testid="lead-plan" className={cls} value={f.plan} onChange={(e) => setF({ ...f, plan: e.target.value })}>
        <option value="">Interested plan (optional)</option>
        {["Starter", "Growth", "Professional", "Enterprise"].map((p) => <option key={p} value={p}>{p}</option>)}
      </select>
      <textarea data-testid="lead-message" rows={3} className={cls} placeholder="What do you need help with? (GST, ITR, audit, payroll…)" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} />
      <button data-testid="lead-submit" disabled={busy} className="w-full bg-[#002FA7] hover:bg-[#00227A] text-white py-2.5 rounded-sm inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
        <PaperPlaneRight size={16} /> {busy ? "Sending…" : "Request Consultation"}
      </button>
    </form>
  );
}

/* ---------------- Calculators ---------------- */
const TDS_SECTIONS = [
  { code: "192A", label: "192A · EPF premature withdrawal", rate: 10, threshold: 50000 },
  { code: "193", label: "193 · Interest on securities", rate: 10, threshold: 10000 },
  { code: "194", label: "194 · Dividend", rate: 10, threshold: 10000 },
  { code: "194A", label: "194A · Interest (non-bank)", rate: 10, threshold: 10000 },
  { code: "194C", label: "194C · Contractor (Ind/HUF)", rate: 1, threshold: 30000 },
  { code: "194C-O", label: "194C · Contractor (Others)", rate: 2, threshold: 30000 },
  { code: "194D", label: "194D · Insurance commission", rate: 2, threshold: 20000 },
  { code: "194DA", label: "194DA · Life insurance payout", rate: 2, threshold: 100000 },
  { code: "194H", label: "194H · Commission / Brokerage", rate: 2, threshold: 20000 },
  { code: "194I-L", label: "194I · Rent (Land/Building)", rate: 10, threshold: 600000 },
  { code: "194I-P", label: "194I · Rent (Plant/Machinery)", rate: 2, threshold: 600000 },
  { code: "194IA", label: "194IA · Transfer of immovable property", rate: 1, threshold: 5000000 },
  { code: "194IB", label: "194IB · Rent by Individual/HUF", rate: 2, threshold: 600000 },
  { code: "194J", label: "194J · Professional fees", rate: 10, threshold: 30000 },
  { code: "194J-T", label: "194J · Technical services", rate: 2, threshold: 30000 },
  { code: "194K", label: "194K · Income from MF units", rate: 10, threshold: 10000 },
  { code: "194M", label: "194M · Payment by Ind/HUF (contract/comm.)", rate: 2, threshold: 5000000 },
  { code: "194N", label: "194N · Cash withdrawal > ₹1cr", rate: 2, threshold: 10000000 },
  { code: "194O", label: "194O · E-commerce operator", rate: 0.1, threshold: 500000 },
  { code: "194Q", label: "194Q · Purchase of goods", rate: 0.1, threshold: 5000000 },
  { code: "194R", label: "194R · Business perquisites", rate: 10, threshold: 20000 },
  { code: "194S", label: "194S · Virtual digital assets (crypto)", rate: 1, threshold: 10000 },
  { code: "194T", label: "194T · Partner remuneration/interest", rate: 10, threshold: 20000 },
  { code: "195", label: "195 · Payment to Non-Resident", rate: 20, threshold: 0 },
  { code: "206C", label: "206C(1H) · TCS on sale of goods", rate: 0.1, threshold: 5000000 },
];

const TABS = [
  { id: "tds", label: "TDS", Icon: Receipt },
  { id: "emi", label: "EMI", Icon: Bank },
  { id: "capgain", label: "Capital Gains", Icon: TrendUp },
  { id: "esipf", label: "ESI / PF", Icon: UsersThree },
  { id: "incometax", label: "Income Tax", Icon: ChartPieSlice },
];

function Field({ label, children }) {
  return <div><label className="label block mb-1">{label}</label>{children}</div>;
}
const numCls = "w-full border border-black/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]";

function ResultRow({ k, v, strong }) {
  return (
    <div className={`flex justify-between py-2 border-b border-black/5 ${strong ? "font-bold text-[#002FA7]" : ""}`}>
      <span>{k}</span><span className="tnum">{v}</span>
    </div>
  );
}

function Calculators() {
  const [tab, setTab] = useState("tds");
  return (
    <div className="card p-0 overflow-hidden">
      <div className="flex flex-wrap border-b border-black/10">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`calc-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-5 py-4 text-sm transition-colors ${tab === t.id ? "bg-[#002FA7] text-white" : "hover:bg-black/5"}`}>
            <t.Icon size={16} /> {t.label}
          </button>
        ))}
      </div>
      <div className="p-6">
        {tab === "tds" && <TdsCalc />}
        {tab === "emi" && <EmiCalc />}
        {tab === "capgain" && <CapGainCalc />}
        {tab === "esipf" && <EsiPfCalc />}
        {tab === "incometax" && <IncomeTaxCalc />}
      </div>
    </div>
  );
}

function TdsCalc() {
  const [sec, setSec] = useState(TDS_SECTIONS[0].code);
  const [amount, setAmount] = useState(100000);
  const [hasPan, setHasPan] = useState(true);
  const s = TDS_SECTIONS.find((x) => x.code === sec);
  const amt = Number(amount) || 0;
  const applies = amt >= s.threshold;
  const rate = hasPan ? s.rate : Math.max(s.rate, 20);
  const tds = applies ? (amt * rate) / 100 : 0;
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div className="space-y-4">
        <Field label="Nature of payment (Section)">
          <select data-testid="tds-section" value={sec} onChange={(e) => setSec(e.target.value)} className={numCls}>
            {TDS_SECTIONS.map((x) => <option key={x.code} value={x.code}>{x.label}</option>)}
          </select>
        </Field>
        <Field label="Payment amount (₹)">
          <input data-testid="tds-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className={numCls} />
        </Field>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" data-testid="tds-pan" checked={hasPan} onChange={(e) => setHasPan(e.target.checked)} /> Payee has PAN
        </label>
        <p className="text-xs text-[#888]">Rates per Income-tax Act, 2025 (FY 2025-26). Threshold: {rupee(s.threshold)}. No PAN → min 20%.</p>
      </div>
      <div data-testid="tds-result" className="bg-black/[0.02] rounded-sm p-5">
        <ResultRow k="Applicable rate" v={`${rate}%`} />
        <ResultRow k="Threshold met?" v={applies ? "Yes" : "No — no TDS"} />
        <ResultRow k="TDS to deduct" v={rupee(tds)} strong />
        <ResultRow k="Net payable to party" v={rupee(amt - tds)} />
      </div>
    </div>
  );
}

function EmiCalc() {
  const [p, setP] = useState(2000000);
  const [rate, setRate] = useState(9);
  const [months, setMonths] = useState(120);
  const P = Number(p) || 0, r = (Number(rate) || 0) / 1200, n = Number(months) || 0;
  const emi = r === 0 ? (n ? P / n : 0) : (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
  const total = emi * n, interest = total - P;
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div className="space-y-4">
        <Field label="Loan amount (₹)"><input data-testid="emi-principal" type="number" value={p} onChange={(e) => setP(e.target.value)} className={numCls} /></Field>
        <Field label="Interest rate (% p.a.)"><input data-testid="emi-rate" type="number" value={rate} onChange={(e) => setRate(e.target.value)} className={numCls} /></Field>
        <Field label="Tenure (months)"><input data-testid="emi-months" type="number" value={months} onChange={(e) => setMonths(e.target.value)} className={numCls} /></Field>
      </div>
      <div data-testid="emi-result" className="bg-black/[0.02] rounded-sm p-5">
        <ResultRow k="Monthly EMI" v={rupee(emi)} strong />
        <ResultRow k="Total interest" v={rupee(interest)} />
        <ResultRow k="Total payment" v={rupee(total)} />
      </div>
    </div>
  );
}

function CapGainCalc() {
  const [type, setType] = useState("equity");
  const [buy, setBuy] = useState(500000);
  const [sell, setSell] = useState(800000);
  const [holding, setHolding] = useState(18);
  const gain = (Number(sell) || 0) - (Number(buy) || 0);
  const m = Number(holding) || 0;
  const isEquity = type === "equity";
  const ltThreshold = isEquity ? 12 : 24;
  const isLong = m > ltThreshold;
  let tax = 0, label = "", note = "";
  if (gain <= 0) { label = "No gain"; }
  else if (isLong) {
    if (isEquity) { const taxable = Math.max(0, gain - 125000); tax = taxable * 0.125; label = "LTCG @ 12.5% (₹1.25L exempt)"; }
    else { tax = gain * 0.125; label = "LTCG @ 12.5% (no indexation)"; }
  } else {
    if (isEquity) { tax = gain * 0.20; label = "STCG @ 20%"; }
    else { tax = 0; label = "STCG at slab rate"; note = "Added to income & taxed at your slab."; }
  }
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div className="space-y-4">
        <Field label="Asset type">
          <select data-testid="cg-type" value={type} onChange={(e) => setType(e.target.value)} className={numCls}>
            <option value="equity">Listed Equity / Equity MF (STT paid)</option>
            <option value="other">Property / Debt / Other</option>
          </select>
        </Field>
        <Field label="Purchase price (₹)"><input data-testid="cg-buy" type="number" value={buy} onChange={(e) => setBuy(e.target.value)} className={numCls} /></Field>
        <Field label="Sale price (₹)"><input data-testid="cg-sell" type="number" value={sell} onChange={(e) => setSell(e.target.value)} className={numCls} /></Field>
        <Field label="Holding period (months)"><input data-testid="cg-holding" type="number" value={holding} onChange={(e) => setHolding(e.target.value)} className={numCls} /></Field>
        <p className="text-xs text-[#888]">Long-term if held &gt; {ltThreshold} months. Rates per current provisions.</p>
      </div>
      <div data-testid="cg-result" className="bg-black/[0.02] rounded-sm p-5">
        <ResultRow k="Capital gain" v={rupee(gain)} />
        <ResultRow k="Classification" v={gain <= 0 ? "—" : (isLong ? "Long-term" : "Short-term")} />
        <ResultRow k="Tax treatment" v={label} />
        <ResultRow k="Estimated tax" v={rupee(tax)} strong />
        {note && <p className="text-xs text-[#888] mt-2">{note}</p>}
      </div>
    </div>
  );
}

function EsiPfCalc() {
  const [basic, setBasic] = useState(25000);
  const [gross, setGross] = useState(35000);
  const b = Number(basic) || 0, g = Number(gross) || 0;
  const pfBase = b; // employee opts on actual basic
  const pfEmp = pfBase * 0.12;
  const epsBase = Math.min(pfBase, 15000);
  const eps = epsBase * 0.0833;
  const epfEmployer = pfEmp - eps > 0 ? pfEmp - eps : pfBase * 0.0367;
  const esiApplicable = g <= 21000;
  const esiEmp = esiApplicable ? g * 0.0075 : 0;
  const esiEmployer = esiApplicable ? g * 0.0325 : 0;
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div className="space-y-4">
        <Field label="Basic + DA (₹/month)"><input data-testid="pf-basic" type="number" value={basic} onChange={(e) => setBasic(e.target.value)} className={numCls} /></Field>
        <Field label="Gross salary (₹/month)"><input data-testid="pf-gross" type="number" value={gross} onChange={(e) => setGross(e.target.value)} className={numCls} /></Field>
        <p className="text-xs text-[#888]">PF 12% each. EPS 8.33% (capped at ₹15,000 wage). ESI applies when gross ≤ ₹21,000 (0.75% employee, 3.25% employer).</p>
      </div>
      <div data-testid="esipf-result" className="bg-black/[0.02] rounded-sm p-5">
        <ResultRow k="PF — Employee (12%)" v={rupee(pfEmp)} />
        <ResultRow k="PF — Employer (EPS+EPF)" v={rupee(eps + epfEmployer)} />
        <ResultRow k="ESI — Employee (0.75%)" v={esiApplicable ? rupee(esiEmp) : "N/A"} />
        <ResultRow k="ESI — Employer (3.25%)" v={esiApplicable ? rupee(esiEmployer) : "N/A"} />
        <ResultRow k="Total monthly contribution" v={rupee(pfEmp + eps + epfEmployer + esiEmp + esiEmployer)} strong />
      </div>
    </div>
  );
}
